import React, { useMemo } from 'react'

import { keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'

import { ConfirmationCard } from './ConfirmationCard'
import { FilterPreviewCard } from './FilterPreviewCard'
import { useCyclingLabel } from './hooks/useCyclingLabel'
import { useScrollAnchor } from './hooks/useScrollAnchor'
import { MessageBubble } from './MessageBubble'
import { getOpeningSuggestionKeys } from './openingSuggestions'
import { OptionsCard } from './OptionsCard'
import { useAiChatBotStore } from '../core/store/useAiChatBotStore'
import { useConversationStore } from '../core/store/useConversationStore'
import { theme } from '@/Atomic/assets/theme'

// --- Keyframes ---

const dotWave = keyframes`
  0%, 60%, 100% { opacity: 0.25; transform: translateY(0); }
  30%            { opacity: 1;    transform: translateY(-3px); }
`

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`

// --- Styled components ---

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-gutter: stable;
  padding: ${theme.space.l};
  display: flex;
  flex-direction: column;
  gap: ${theme.space.s};
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
`

const Thinking = styled.div`
  align-self: flex-start;
  display: flex;
  align-items: center;
  gap: ${theme.space.xs};
  padding: ${theme.space.xs} 0;
  color: ${theme.colors.text.secondary};
  font-size: 0.875rem;
`

const ThinkingLabel = styled.span`
  color: ${theme.colors.text.secondary};
  animation: ${fadeIn} 0.3s ease;
`

const ThinkingDot = styled.span<{ delay: number }>`
  display: inline-block;
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: currentColor;
  margin-left: 2px;
  vertical-align: baseline;
  position: relative;
  bottom: 0px;
  animation: ${dotWave} 1.2s cubic-bezier(0.4, 0, 0.6, 1)
    ${({ delay }) => delay}s infinite;
`

const SyntheticUserMessage = styled.div`
  max-width: 90%;
  padding: ${theme.space.s} ${theme.space.m};
  border-radius: ${theme.radii.lg};
  font-size: 0.875rem;
  line-height: 1.5;
  align-self: flex-end;
  background: ${theme.colors.background.subtle};
  color: ${theme.colors.text.primary};
  border-bottom-right-radius: ${theme.space['2xs']};
  white-space: pre-wrap;
  word-break: break-word;
`

const SuggestionList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${theme.space.xs};
`

// Mirrors the RunPromptButton chip in MarkdownRenderer — Badge-like
const SuggestionChip = styled.button`
  display: inline-flex;
  align-items: center;
  line-height: 1.4;
  border-radius: 3px;
  padding: ${theme.space.xs} ${theme.space.s};
  background: ${theme.colors.background.subtle};
  border: none;
  color: ${theme.colors.text.primary};
  font-size: 0.875rem;
  font-weight: 400;
  cursor: pointer;
  width: fit-content;
  text-align: left;
  transition: background-color 0.15s ease;

  &:hover {
    background: ${theme.colors.background.active};
  }
`

// Brand easter egg — English only, intentionally untranslated
const BRAND_LABEL = 'Just relating'

// --- Component ---

export function MessageList() {
  const { t } = useTranslation()
  const {
    messages,
    isProcessing,
    isThinking,
    activeToolCalls,
    pendingToolCalls,
  } = useConversationStore(
    ({
      messages,
      isProcessing,
      isThinking,
      activeToolCalls,
      pendingToolCalls,
    }) => ({
      messages,
      isProcessing,
      isThinking,
      activeToolCalls,
      pendingToolCalls,
    }),
  )
  const { resolveToolCall, submit } = useConversationStore.getState()
  const firstUserMessageOverride = useAiChatBotStore(
    (s) => s.activeConfig?.initialDisplayMessage,
  )

  const { containerRef, handleScroll } = useScrollAnchor(
    messages,
    activeToolCalls,
    pendingToolCalls,
  )

  const thinkingLabels = [
    ...(t('components.aiChatBot.thinkingLabels', {
      returnObjects: true,
    }) as string[]),
    BRAND_LABEL,
  ]

  const firstUserIndex = messages.findIndex((m) => m.role === 'user')

  const thinkingText = useCyclingLabel(thinkingLabels, isThinking)

  // Derive synthetic user-selection labels from message history at render time.
  // Works for all history (old and new) — no store changes required.
  const selectionLabels = useMemo(() => {
    const optionsByToolCallId = new Map<
      string,
      Array<{ value: string; label: string }>
    >()
    for (const msg of messages) {
      if (msg.role !== 'assistant') continue
      for (const tc of msg.tool_calls ?? []) {
        if (tc.type !== 'function' || tc.function.name !== 'show_options') {
          continue
        }
        try {
          const args = JSON.parse(tc.function.arguments) as {
            options?: Array<{ value: string; label: string }>
          }
          if (Array.isArray(args.options)) {
            optionsByToolCallId.set(tc.id, args.options)
          }
        } catch {
          // malformed args — skip
        }
      }
    }

    const result = new Map<string, string>()
    for (const msg of messages) {
      if (msg.role !== 'tool') continue
      if (!msg.tool_call_id || typeof msg.content !== 'string') {
        continue
      }
      try {
        const res = JSON.parse(msg.content) as
          | { selected?: string[]; cancelled?: boolean }
          | { startDate?: string; endDate?: string; cancelled?: boolean }
        if (res.cancelled) continue
        if ('selected' in res && Array.isArray(res.selected)) {
          const opts = optionsByToolCallId.get(msg.tool_call_id)
          const labels = res.selected
            .map((v) => opts?.find((o) => o.value === v)?.label ?? v)
            .join(', ')
          if (labels) result.set(msg.tool_call_id, labels)
        } else if ('startDate' in res && res.startDate && res.endDate) {
          result.set(
            msg.tool_call_id,
            t('components.aiChatBot.dateRange.selectionSummary', {
              start: res.startDate,
              end: res.endDate,
            }),
          )
        }
      } catch {
        // malformed content — skip
      }
    }
    return result
  }, [messages, t])

  return (
    <MessagesContainer ref={containerRef} onScroll={handleScroll}>
      {messages.length === 0 && !isProcessing && (
        <SuggestionList>
          {getOpeningSuggestionKeys().map((key) => (
            <SuggestionChip
              key={key}
              type="button"
              onClick={() => submit(t(key))}
            >
              {t(key)}
            </SuggestionChip>
          ))}
        </SuggestionList>
      )}
      {messages.map((message, index) => {
        const displayOverride =
          firstUserMessageOverride && index === firstUserIndex
            ? firstUserMessageOverride
            : undefined
        const isLastMsg = index === messages.length - 1

        const toolMsg = message.role === 'tool' ? message : null
        const syntheticLabel = toolMsg?.tool_call_id
          ? selectionLabels.get(toolMsg.tool_call_id)
          : undefined

        return (
          <React.Fragment key={index}>
            {syntheticLabel && (
              <SyntheticUserMessage>{syntheticLabel}</SyntheticUserMessage>
            )}
            <MessageBubble
              message={message}
              displayOverride={displayOverride}
              isStreaming={isProcessing && isLastMsg}
            />
          </React.Fragment>
        )
      })}
      {pendingToolCalls.map((pending) => {
        if (pending.type === 'confirm') {
          return (
            <ConfirmationCard
              key={pending.toolCallId}
              pending={pending}
              onSubmit={(result) => resolveToolCall(pending.toolCallId, result)}
            />
          )
        }
        if (pending.type === 'filterPreview') {
          return (
            <FilterPreviewCard
              key={pending.toolCallId}
              pending={pending}
              onSubmit={(result) => resolveToolCall(pending.toolCallId, result)}
            />
          )
        }
        return (
          <OptionsCard
            key={pending.toolCallId}
            pending={pending}
            multiSelect={
              pending.type === 'options' ? pending.multiSelect : false
            }
            onSubmit={(result) => resolveToolCall(pending.toolCallId, result)}
          />
        )
      })}
      {isThinking && (
        <Thinking>
          <ThinkingLabel key={thinkingText} aria-label={`${thinkingText}...`}>
            {thinkingText}
            <ThinkingDot delay={0} aria-hidden="true" />
            <ThinkingDot delay={0.15} aria-hidden="true" />
            <ThinkingDot delay={0.3} aria-hidden="true" />
          </ThinkingLabel>
        </Thinking>
      )}
    </MessagesContainer>
  )
}
